import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React from 'react'
import invariant from 'tiny-invariant'

import Select from '@/components/select/Select'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useUserCompanies } from '@/hooks/useUserCompanies'
import { faInfoCircle } from '@/lib/fontAwesomePro/regular'
import { CompanyEntity } from '@/types/entities'

export type CompanyOption = { '@id': IRI } & Pick<
  CompanyEntity,
  'id' | 'presentationName'
> & {
    intellifloIntegration?: CompanyEntity['intellifloIntegration'] | null
    plannrIntegration?: CompanyEntity['plannrIntegration']
    xplanIntegration?: CompanyEntity['xplanIntegration']
  }

export type SelectUserCompanyProps = Omit<
  React.ComponentProps<typeof Select>,
  'onChange' | 'isOptionDisabled'
> & {
  isOptionDisabled?: (option: CompanyOption) => boolean
  renderDisabledOptionTooltip?: (
    option: CompanyOption,
  ) => React.ReactElement | string
  companyOptions?: CompanyOption[]
  onChange: (company: CompanyOption | null) => void
}

export interface SelectUserCompanyOption {
  label: string
  value: number
  isDisabled: boolean
}

const SelectUserCompany: React.FC<SelectUserCompanyProps> = ({
  onChange,
  isOptionDisabled,
  renderDisabledOptionTooltip,
  companyOptions,
  ...props
}) => {
  const userCompanies = useUserCompanies()
  const companies = companyOptions || userCompanies

  const options: SelectUserCompanyOption[] = companies.map(
    (company): SelectUserCompanyOption => ({
      label: company.presentationName,
      value: company.id,
      isDisabled: isOptionDisabled ? isOptionDisabled(company) : false,
    }),
  )

  function formatOptionLabel(option: SelectUserCompanyOption) {
    const company = companies.find((company) => company.id === option.value)

    invariant(company, `Failed to find company with ID: ${option.value}`)

    const disabledOptionTooltip = renderDisabledOptionTooltip
      ? renderDisabledOptionTooltip(company)
      : undefined

    const isDisabled = isOptionDisabled ? isOptionDisabled(company) : false

    return (
      <>
        {company.presentationName}

        {isDisabled && disabledOptionTooltip && (
          <Tooltip>
            <TooltipTrigger asChild>
              <FontAwesomeIcon
                icon={faInfoCircle}
                className="text-disabled relative ml-1 inline-block"
                style={{ fontSize: '14px', top: '1px' }}
              />
            </TooltipTrigger>
            <TooltipContent>{disabledOptionTooltip}</TooltipContent>
          </Tooltip>
        )}
      </>
    )
  }

  return (
    <Select
      options={options}
      formatOptionLabel={(option) =>
        // TODO: Not sure how to avoid the type assertion here
        formatOptionLabel(option as SelectUserCompanyOption)
      }
      onChange={(option) => {
        if (!option) {
          onChange(null)

          return
        }

        // TODO: Not sure how to avoid the type assertion here
        const typedOption = option as SelectUserCompanyOption

        const company = companies.find(
          (company) => company.id === typedOption.value,
        )
        invariant(
          company,
          `Failed to find company with ID: ${typedOption.value}`,
        )

        onChange(company)
      }}
      {...props}
    />
  )
}

export default SelectUserCompany
